import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CssBaseline, Grid, Paper, Typography, WithStyles, withStyles } from "@material-ui/core";
import { styles } from "./SalesRegistrationView.styles";
import * as UserRegistrationAction from "../../../../stores/user/UserRegistrationAction";
import { RegisterForm } from "../../registration/RegisterForm";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import { PageBanner } from "../../../components/PageBanner/PageBanner";
import SubscriptionModel from "../../../../models/subscription/SubscriptionModel";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import * as SubscriptionAction from "../../../../stores/subscriptions/SubscriptionAction";
import { IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import { consolidateDateToNumber } from "../../../../utilities/dateTimeUtils";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import UserDetailsModel from "../../../../models/user/UserDetailsModel";
import * as UserDetailsAction from "../../../../stores/user/UserDetailsAction";
import { AutocompleteUserDropdown } from "../../../components/FormComponents/AutocompleteUserDropdown";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton/ToggleButton";
import { SalesSubscriptionAssignment } from "../../../components/SalesSubscriptionAssignment/SalesSubscriptionAssignment";
import { UserDetails } from "../../../components/UserDetails/UserDetails";
import { IAgrees } from "../../AcceptTermsConditionsComponent";
import { selectVisibleCountries } from "../../../../selectors/countries/CountriesSelector";

const IIGF_VENUE = "IIGF";
const KNEIP_VENUE = "KNEIP";

const EXISTING_USER = "EXISTING_USER";
const NEW_USER = "NEW_USER";
export const USER_TYPE = [EXISTING_USER, NEW_USER] as const;

export type UserType = (typeof USER_TYPE)[number];

interface IOption {
    id: UserType;
    title: string;
    sendButtonTitle: string;
    isSendButtonEnabled: () => boolean;
}

const agreeTypes: IAgrees = {
    agreeClientLegal: false
};

export interface ISalesRegistrationViewProps extends WithStyles<typeof styles> {}

const SalesRegistrationViewPure: React.FunctionComponent<ISalesRegistrationViewProps> = ({ classes }) => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const options: IOption[] = [
        {
            id: NEW_USER,
            title: "Register a new user",
            sendButtonTitle: "Register a new user and entitle the subscription",
            isSendButtonEnabled: () => {
                return (
                    selectedSubscriptions.length > 0 &&
                    tempDateRange.length === 1 &&
                    formIsValid() &&
                    errors.length === 0
                );
            }
        },
        {
            id: EXISTING_USER,
            title: "Select existing user",
            sendButtonTitle: "Entitle the subscription",
            isSendButtonEnabled: () => {
                return selectedSubscriptions.length > 0 && tempDateRange.length === 1 && userId !== null;
            }
        }
    ];

    const [errors, setErrors] = useState([]);
    const [userType, setUserType] = useState<UserType>(NEW_USER);
    const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
    const [userId, setUserId] = React.useState<string | null>(null);
    const [registerError, setRegisterError] = useState<string>("");

    const isRequestingDateRanges = useSelector(state =>
        selectRequesting(state, [SubscriptionAction.REQUEST_ALL_SUBSCRIPTIONS])
    );
    const userDateRangesFromState = useSelector(state => state.subscriptions?.allUserDateRanges);
    const subscriptionTypesFromState = useSelector(state => state.subscriptions?.allSubscriptions);
    const productsInfo = useSelector(state => state.products);

    useEffect(() => {
        dispatch(SubscriptionAction.requestAllSubscriptionTypes());
        dispatch(UserDetailsAction.requestAllUsersDetails());
    }, [dispatch]);

    useEffect(() => {
        if (userId) {
            dispatch(UserDetailsAction.requestUser(userId));
            dispatch(SubscriptionAction.requestAllUserDateRanges(userId));
        }
    }, [dispatch, userId]);

    let alreadySubscribedSubscriptionTypes: number[];
    if (userDateRangesFromState) {
        alreadySubscribedSubscriptionTypes = Object.values(userDateRangesFromState)?.map(
            (dR: any) => dR.subscriptionTypeId
        );
    }

    const getSubscriptionTypeVenues = (productsInfo, sTId: number): string[] => {
        const subscriptionProduct = productsInfo?.products?.find(p => p?.order?.subscriptionTypeId?.[0] === sTId);
        return subscriptionProduct?.venue;
    };

    let addableSubscriptionTypes = [];
    if (subscriptionTypesFromState) {
        addableSubscriptionTypes = Object.values(subscriptionTypesFromState)
            ?.filter((subscriptionType: SubscriptionModel) => !subscriptionType.isFilterable)
            ?.filter(
                (subscriptionType: SubscriptionModel) =>
                    !getSubscriptionTypeVenues(productsInfo, subscriptionType.id)?.some(venue =>
                        [IIGF_VENUE, KNEIP_VENUE].includes(venue)
                    )
            )
            ?.map((subscriptionType: SubscriptionModel) => ({
                subscriptionTypeId: subscriptionType.id,
                name: subscriptionType.name,
                description: subscriptionType.description
            }));
        if (userType === EXISTING_USER && alreadySubscribedSubscriptionTypes) {
            addableSubscriptionTypes = addableSubscriptionTypes?.filter(
                (subscriptionType: any) =>
                    !alreadySubscribedSubscriptionTypes.includes(subscriptionType.subscriptionTypeId)
            );
        }
    }

    const countries = useSelector(state => selectVisibleCountries(state));
    const userDetails = useSelector(state => state.userDetails?.requestedUser);

    const [details, setDetails] = React.useState({
        email: "",
        firstName: "",
        lastName: "",
        jobPosition: "",
        phone: "",
        mobilePhone: "",
        company: "",
        tin: "",

        addressLine1: "",
        addressLine2: "",
        postcode: "",
        city: "",
        country: "",

        deliveryAddressLine1: "",
        deliveryAddressLine2: "",
        deliveryPostcode: "",
        deliveryCity: "",
        deliveryCountry: "",
        isCreatedByAdmin: true
    });

    const allUsersFromState = useSelector(state => state.userDetails?.all);

    let allUsers;
    if (allUsersFromState) {
        allUsers = Object.values(allUsersFromState)?.map((user: UserDetailsModel) => ({
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
        }));
    }

    const handleChange = (event, isFieldValid) => {
        const name = event.target.name;
        const value = event.target.value;

        setDetails({
            ...details,
            [name]: value
        });

        if (isFieldValid) {
            setErrors(errors.filter(e => e !== name));
        } else {
            setErrors(errors.concat(name));
        }
    };

    const handleCountry = event => {
        if (event.target.value !== "") {
            setDetails({
                ...details,
                [event.target.name]: event.target.value
            });
        }
    };

    const [anotherDeliveryAddress, setAnotherDeliveryAddress] = React.useState(false);
    const [tempDateRange, setTempDateRange] = useState<IUniversalTableData>([]);

    const handleEditDateRange = (index, newData) => {
        const updatedDateRange = {
            startDate: consolidateDateToNumber(newData.startDate),
            visibilityStartDate: consolidateDateToNumber(newData.visibilityStartDate)
        };
        setTempDateRange([updatedDateRange]);
    };

    const handleAddDateRange = newData => {
        const newDateRange = {
            startDate: consolidateDateToNumber(newData.startDate),
            visibilityStartDate: consolidateDateToNumber(newData.visibilityStartDate),
            trial: true,
            salesTrial: true
        };
        setTempDateRange([newDateRange]);
    };

    const handleDeleteFinished = () => {
        setTempDateRange([]);
    };

    const handleMultipleSubscriptionsSelection = (event, value) => {
        setSelectedSubscriptions(value);
    };

    const handleUserChange = user => {
        setUserId(user?.userId);
    };

    const [isJustRegistered, setIsJustRegistered] = useState<boolean>(false);
    const registeredUser = useSelector(state => state.userDetails.registeredUser);

    useEffect(() => {
        // user registered
        if (isJustRegistered && registeredUser) {
            setIsJustRegistered(false);

            const subscriptionsNames = Object.values(selectedSubscriptions).map(subscription => subscription.name);

            let sendData = {
                userId: registeredUser.id,
                subscriptions: subscriptionsNames,
                ...(tempDateRange[0].startDate && {
                    startDate: consolidateDateToNumber(tempDateRange[0].startDate)
                }),
                ...(tempDateRange[0].visibilityStartDate && {
                    visibilityStartDate: consolidateDateToNumber(tempDateRange[0].visibilityStartDate)
                }),
                trial: true,
                salesTrial: true
            };

            dispatch(SubscriptionAction.createMultipleSubscriptions(sendData))
                .then(() => {
                    enqueueSnackbar("Subscriptions assigned", { variant: ToastStatusEnum.Info });
                    history.push({
                        pathname: "/admin/sales-registration/success",
                        state: NEW_USER
                    });
                })
                .catch(e => {
                    setRegisterError(e.message);
                });
        }
        // eslint-disable-next-line
    }, [isJustRegistered, registeredUser]);

    const handleSubmit = () => {
        if (userType === NEW_USER) {
            let sendDetails = details;
            if (anotherDeliveryAddress === false) {
                sendDetails.deliveryAddressLine1 = details.addressLine1;
                sendDetails.deliveryAddressLine2 = details.addressLine2;
                sendDetails.deliveryCity = details.city;
                sendDetails.deliveryPostcode = details.postcode;
                sendDetails.deliveryCountry = details.country;
            }
            dispatch(UserRegistrationAction.registerUser(sendDetails))
                .then(() => {
                    enqueueSnackbar("User registered", { variant: ToastStatusEnum.Info });
                    setRegisterError("");
                    setIsJustRegistered(true);
                    if (registeredUser) {
                    }
                })
                .catch(e => {
                    setRegisterError(e.message);
                });
        } else {
            const subscriptionsNames = Object.values(selectedSubscriptions).map(subscription => subscription.name);

            let sendData = {
                userId: userId,
                subscriptions: subscriptionsNames,
                ...(tempDateRange[0].startDate && { startDate: consolidateDateToNumber(tempDateRange[0].startDate) }),
                ...(tempDateRange[0].visibilityStartDate && {
                    visibilityStartDate: consolidateDateToNumber(tempDateRange[0].visibilityStartDate)
                }),
                trial: true,
                salesTrial: true
            };

            dispatch(SubscriptionAction.createMultipleSubscriptions(sendData))
                .then(() => {
                    enqueueSnackbar("Subscriptions assigned", { variant: ToastStatusEnum.Info });
                    history.push({
                        pathname: "/admin/sales-registration/success",
                        state: EXISTING_USER
                    });
                })
                .catch(e => {
                    setRegisterError(e.message);
                });
        }
    };

    const [agrees, setAgrees] = React.useState(
        agreeTypes || {
            agreeClientLegal: false
        }
    );

    const handleAgree = event => {
        setAgrees({
            ...agrees,
            [event.target.name]: !agrees[event.target.name]
        });
    };

    const formIsValid = (): boolean => {
        let isValid =
            !errors.includes("firstName") &&
            !errors.includes("lastName") &&
            !errors.includes("addressLine1") &&
            !errors.includes("postcode") &&
            !errors.includes("city") &&
            !errors.includes("company") &&
            details.firstName?.length !== 0 &&
            details.lastName?.length !== 0 &&
            details.addressLine1?.length !== 0 &&
            details.postcode?.length !== 0 &&
            details.city?.length !== 0 &&
            details.company?.length !== 0 &&
            details.country?.length !== 0;

        isValid = isValid && agrees.agreeClientLegal && !errors.includes("email") && details.email?.length !== 0;

        return isValid;
    };

    return (
        <>
            <PageBanner title="User registration & Trial subscription entitlement for sales" />
            <CenteredContainer>
                <CssBaseline />
                <Grid container spacing={3}>
                    <Grid item xs={4}>
                        <div className={classes.sectionInput}>
                            <ToggleButtonGroup
                                value={userType}
                                exclusive
                                onChange={(event, newValue) => setUserType(newValue)}
                                aria-label="text formatting"
                                className={classes.buttonGroup}
                            >
                                {options.map(o => (
                                    <ToggleButton
                                        key={o.id}
                                        value={o.id}
                                        aria-label="bold"
                                        className={classes.toggleButton}
                                    >
                                        {o.title}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </div>
                    </Grid>
                </Grid>

                {userType === EXISTING_USER ? (
                    <>
                        <div>
                            {allUsers && (
                                <>
                                    <Typography variant="h4">Select user from the list</Typography>
                                    <AutocompleteUserDropdown
                                        allUsers={allUsers}
                                        selectedUserId={userId}
                                        handleUserChange={handleUserChange}
                                    />
                                </>
                            )}
                        </div>
                        {userId && userDetails !== undefined && (
                            <UserDetails countries={countries} userDetails={userDetails}></UserDetails>
                        )}
                    </>
                ) : (
                    <Paper className={classes.formBox}>
                        <Grid container spacing={1}>
                            <RegisterForm
                                type="addNewUserAdmin"
                                details={details}
                                registerError={registerError}
                                handleChange={handleChange}
                                handleCountry={handleCountry}
                                allCountries={countries}
                                anotherDeliveryAddress={anotherDeliveryAddress}
                                setAnotherDeliveryAddress={setAnotherDeliveryAddress}
                                agrees={agrees}
                                handleAgree={handleAgree}
                            />
                        </Grid>
                    </Paper>
                )}
                {((userType === EXISTING_USER && userId !== null) || userType === NEW_USER) && (
                    <div>
                        <SalesSubscriptionAssignment
                            selectedSubscriptionTypes={selectedSubscriptions}
                            isRequestingDateRanges={isRequestingDateRanges}
                            addableSubscriptionTypes={addableSubscriptionTypes}
                            tempDateRange={tempDateRange}
                            handleEditDateRange={handleEditDateRange}
                            handleAddDateRange={handleAddDateRange}
                            handleDeleteFinished={handleDeleteFinished}
                            handleMultipleSubscriptionsSelection={handleMultipleSubscriptionsSelection}
                        ></SalesSubscriptionAssignment>

                        <div>
                            <Typography variant="body2" gutterBottom className={classes.note}>
                                Please note that the end date will be automatically set to 5 days after the start date.
                                The same applies for visibility dates.{" "}
                            </Typography>
                        </div>

                        {registerError && (
                            <Grid item xs={12}>
                                <Typography variant="h5" className={classes.errorText}>
                                    {registerError}
                                </Typography>
                            </Grid>
                        )}

                        <BlueButton
                            disabled={!options?.find(o => o.id === userType)?.isSendButtonEnabled()}
                            onClick={handleSubmit}
                        >
                            {options?.find(o => o.id === userType)?.sendButtonTitle}
                        </BlueButton>
                    </div>
                )}
            </CenteredContainer>
        </>
    );
};

export const SalesRegistrationView = withStyles(styles)(SalesRegistrationViewPure);
